import { useCallback } from 'react';
import { useRecoilCallback } from 'recoil';
import type { CallbackInterface } from 'recoil';
import { dehydratedHangingProtocol, viewportsConfigurationsState } from './state';
import { pruneBuckets, renameBucket, writeBucket } from './stepBuckets';
import type { BucketedViewports, StepBucketKey } from './stepBuckets';
import type { DehydratedViewportsConfigurations } from './types';

/**
 * The single owner of writes to `viewportsConfigurationsState` and `dehydratedHangingProtocol`.
 *
 * Both atoms used to be written from eight places, each of which had to remember on its own that the
 * value it was writing belonged to one clinical step and not to the case. Six of them did not, which
 * is the fifth instance of the VX-4624 family (VX-4624, VX-4732, VX-4768, VX-4772). Routing every
 * write through named intents here, and failing the build on writes from anywhere else
 * (`viewportStepWriters.guard.test.ts`), removes the class rather than the instances.
 *
 * Every writer is a free function taking Recoil's `set` rather than a hook owning its own
 * `useRecoilCallback`. That is the load-bearing part: a caller that already holds a batch can compose
 * these into it, so the viewport override and the step atom it belongs with land in one transaction.
 * Splitting them across batches makes the grid render a frame stale -- exactly what the four tickets
 * above were. The hook exists for call sites that have no batch of their own.
 */

type StepScopedArgs = {
  caseId: string;
  bucket: StepBucketKey;
};

/** Records the user's viewport overrides for one step, leaving every other step's alone. */
export function setStepViewportOverrides(
  set: CallbackInterface['set'],
  {
    caseId,
    bucket,
    overrides,
  }: StepScopedArgs & { overrides: Nullable<DehydratedViewportsConfigurations> }
): void {
  set(viewportsConfigurationsState(caseId), (prev) => ({
    created: prev.created,
    updated: Date.now(),
    byStep: writeBucket(prev.byStep, bucket, overrides),
  }));
}

/**
 * Drops one step's viewport overrides.
 *
 * Writes `{}` rather than deleting the key: an empty bucket and a missing bucket read identically, and
 * keeping the key makes "this step was cleared" visible when debugging a persisted record.
 */
export function clearStepViewportOverrides(
  set: CallbackInterface['set'],
  { caseId, bucket }: StepScopedArgs
): void {
  setStepViewportOverrides(set, { caseId, bucket, overrides: {} });
}

/**
 * Puts back overrides captured before a destructive operation.
 *
 * Distinct from {@link setStepViewportOverrides} only in name, and that is the point -- an UNDO reads
 * differently at the call site from a user edit, and the two have been confused before.
 */
export function restoreStepViewportOverrides(
  set: CallbackInterface['set'],
  {
    caseId,
    bucket,
    overrides,
  }: StepScopedArgs & { overrides: Nullable<DehydratedViewportsConfigurations> }
): void {
  setStepViewportOverrides(set, { caseId, bucket, overrides });
}

/**
 * Publishes the viewport assignments resolved from the hanging protocol for one step.
 *
 * The secondary window learns this only by broadcast -- `useHangingProtocol` returns early on
 * `!isPrimaryWindow` before it can resolve anything -- so this atom cannot be replaced by a selector
 * over the step list, however derived it looks.
 */
export function publishStepHangingProtocol(
  set: CallbackInterface['set'],
  {
    caseId,
    bucket,
    dehydrated,
  }: StepScopedArgs & { dehydrated: Nullable<DehydratedViewportsConfigurations> }
): void {
  set(dehydratedHangingProtocol(caseId), (prev) => ({
    byStep: writeBucket(prev.byStep, bucket, dehydrated),
  }));
}

/**
 * Moves both atoms' buckets from a temporary step smid to the real one the server assigned.
 *
 * An added step exists under a `tempSmid` until its mutation resolves, and a user can drag a series
 * into it in that window. Without the move the drop stays stranded under a key nothing will ever read.
 */
export function renameStepBucket(
  set: CallbackInterface['set'],
  { caseId, from, to }: { caseId: string; from: StepBucketKey; to: StepBucketKey }
): void {
  set(viewportsConfigurationsState(caseId), (prev) => ({
    created: prev.created,
    updated: Date.now(),
    byStep: renameBucket(prev.byStep, from, to),
  }));
  set(dehydratedHangingProtocol(caseId), (prev) => ({
    byStep: renameBucket(prev.byStep, from, to),
  }));
}

/**
 * Drops buckets whose step no longer exists in the live step list.
 *
 * Cheap enough to call on every step-list change: `pruneBuckets` returns its input by reference when
 * nothing was dropped, so an unchanged record does not churn the atom or the persister.
 */
export function pruneStepBuckets(
  set: CallbackInterface['set'],
  { caseId, liveStepSmids }: { caseId: string; liveStepSmids: readonly string[] }
): void {
  set(viewportsConfigurationsState(caseId), (prev) => {
    const byStep = pruneBuckets(prev.byStep, liveStepSmids);
    return byStep === prev.byStep ? prev : { created: prev.created, updated: Date.now(), byStep };
  });
  set(dehydratedHangingProtocol(caseId), (prev) => {
    const byStep = pruneBuckets(prev.byStep, liveStepSmids);
    return byStep === prev.byStep ? prev : { byStep };
  });
}

/**
 * Empties both atoms for a case.
 *
 * Switching to a different protocol on the same case leaves every bucket keyed by a step smid that is
 * no longer addressable. Prune cannot reach them at that moment -- the new step list has not arrived --
 * so the switch clears wholesale and the next resolve repopulates.
 */
export function clearAllStepBuckets(
  set: CallbackInterface['set'],
  { caseId }: { caseId: string }
): void {
  set(viewportsConfigurationsState(caseId), (prev) => ({
    created: prev.created,
    updated: Date.now(),
    byStep: {},
  }));
  set(dehydratedHangingProtocol(caseId), { byStep: {} });
}

/**
 * Discards every step's viewport overrides for a case, in the one form that crosses windows.
 *
 * Distinct from {@link clearAllStepBuckets}, and the difference is the whole point. That one empties
 * the record because the buckets are keyed by steps that are no longer addressable, so absence is the
 * right answer locally -- and `mergeBucketsFromBroadcast` deliberately refuses to let absence delete
 * anything in a peer, because a window that is merely behind would otherwise delete buckets it has not
 * learned about yet. Reset All is the opposite intent: every step still exists, and the user has
 * discarded their edits on all of them. That is state to publish, not an absence to infer.
 *
 * So this writes each bucket key holding `{}`, which is exactly the form the merge already documents
 * as crossing ("a deliberate clear still crosses, because clearing writes the bucket key holding
 * `{}`"). Measured, because the wholesale form silently did not (VX-4987): with a peer holding
 * `{stepA: …, stepB: …}`, an incoming `{}` leaves both intact, while `{stepA: {}, stepB: {}}` clears
 * both. Worse than a one-way miss -- the peer keeps broadcasting its own record, so the union merge
 * hands the discarded override *back* to the window that just reset.
 *
 * `buckets` names the steps the caller knows about, unioned with whatever the record already holds:
 * the record covers steps this window has visited, and the caller's step list covers the ones it has
 * not.
 *
 * `dehydratedHangingProtocol` is still emptied wholesale and stays local, deliberately. Its merge
 * carries `rejectLessResolved`, so a bucket holding `{}` is dropped in favour of the peer's resolved
 * value and a per-key clear could not cross even if it were written that way. It does not need to:
 * that atom is derived, and the peer's own publish effect rebuilds the arriving step's bucket from
 * the reverted steps, which do cross.
 */
export function discardAllStepViewportOverrides(
  set: CallbackInterface['set'],
  { caseId, buckets }: { caseId: string; buckets: readonly StepBucketKey[] }
): void {
  set(viewportsConfigurationsState(caseId), (prev) => {
    const cleared: BucketedViewports = {};
    for (const key of [...Object.keys(prev.byStep), ...buckets]) {
      cleared[key] = {};
    }
    return { created: prev.created, updated: Date.now(), byStep: cleared };
  });
  set(dehydratedHangingProtocol(caseId), { byStep: {} });
}

/**
 * The same eight writers, bound to a Recoil callback for call sites that have no batch of their own.
 *
 * Deliberately does not read the current case or the active bucket: a hook that resolved those from
 * context would throw wherever that context is absent, which is a third of the viewer suite. Callers
 * pass both.
 */
export function useViewportStepWriters(): {
  setStepViewportOverrides: (
    args: StepScopedArgs & { overrides: Nullable<DehydratedViewportsConfigurations> }
  ) => void;
  clearStepViewportOverrides: (args: StepScopedArgs) => void;
  restoreStepViewportOverrides: (
    args: StepScopedArgs & { overrides: Nullable<DehydratedViewportsConfigurations> }
  ) => void;
  publishStepHangingProtocol: (
    args: StepScopedArgs & { dehydrated: Nullable<DehydratedViewportsConfigurations> }
  ) => void;
  renameStepBucket: (args: { caseId: string; from: StepBucketKey; to: StepBucketKey }) => void;
  pruneStepBuckets: (args: { caseId: string; liveStepSmids: readonly string[] }) => void;
  clearAllStepBuckets: (args: { caseId: string }) => void;
  discardAllStepViewportOverrides: (args: {
    caseId: string;
    buckets: readonly StepBucketKey[];
  }) => void;
} {
  const withSet = useRecoilCallback(
    ({ set }) =>
      (fn: (s: CallbackInterface['set']) => void) => {
        fn(set);
      },
    []
  );

  return {
    setStepViewportOverrides: useCallback(
      (args) => withSet((set) => setStepViewportOverrides(set, args)),
      [withSet]
    ),
    clearStepViewportOverrides: useCallback(
      (args) => withSet((set) => clearStepViewportOverrides(set, args)),
      [withSet]
    ),
    restoreStepViewportOverrides: useCallback(
      (args) => withSet((set) => restoreStepViewportOverrides(set, args)),
      [withSet]
    ),
    publishStepHangingProtocol: useCallback(
      (args) => withSet((set) => publishStepHangingProtocol(set, args)),
      [withSet]
    ),
    renameStepBucket: useCallback(
      (args) => withSet((set) => renameStepBucket(set, args)),
      [withSet]
    ),
    pruneStepBuckets: useCallback(
      (args) => withSet((set) => pruneStepBuckets(set, args)),
      [withSet]
    ),
    clearAllStepBuckets: useCallback(
      (args) => withSet((set) => clearAllStepBuckets(set, args)),
      [withSet]
    ),
    discardAllStepViewportOverrides: useCallback(
      (args) => withSet((set) => discardAllStepViewportOverrides(set, args)),
      [withSet]
    ),
  };
}
